import {
  type UseTrackAgentElicitationRequestedParams,
  type UseTrackAgentElicitationRespondedParams,
  type WorkspaceAgentGuidedFlowEditId,
  type WorkspaceAgentGuidedFlowEntry,
} from '@air/analytics';

import { type GuidedVariantEditId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import { WORKSPACE_AGENT_ELICITATION_STEP_INDEX } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentElicitationAnalytics';

const UPLOADED_BATCH_VARIANTS_SUGGESTION_ID = 'uploaded-batch-variants';

const GUIDED_VARIANT_ANALYTICS_EDIT_ID = {
  grayscale: 'grayscale',
  'remove-background': 'remove_background',
  resize: 'scale_or_crop',
  'rounded-corners': 'rounded_corners',
  'smart-resize': 'smart_resize',
  watermark: 'watermark',
} as const satisfies Record<GuidedVariantEditId, WorkspaceAgentGuidedFlowEditId>;

export const toWorkspaceAgentGuidedVariantsAnalyticsEditId = (
  editId: GuidedVariantEditId,
): WorkspaceAgentGuidedFlowEditId => GUIDED_VARIANT_ANALYTICS_EDIT_ID[editId];

/** Zero-state and onboarding chips share this. An uploaded batch uses its own suggestion id. */
export const getWorkspaceAgentGuidedVariantsOpenedEntryForSuggestion = (
  suggestionId: string,
): WorkspaceAgentGuidedFlowEntry =>
  suggestionId === UPLOADED_BATCH_VARIANTS_SUGGESTION_ID ? 'uploaded_batch' : 'zero_state';

type BuildWorkspaceAgentGuidedFlowElicitationParams = {
  chatId: string | null;
};

type BuildWorkspaceAgentGuidedFlowElicitationRequestedParams = BuildWorkspaceAgentGuidedFlowElicitationParams & {
  entry: WorkspaceAgentGuidedFlowEntry;
};

type BuildWorkspaceAgentGuidedFlowElicitationRespondedParams = BuildWorkspaceAgentGuidedFlowElicitationParams & {
  editId: WorkspaceAgentGuidedFlowEditId;
};

export const buildWorkspaceAgentGuidedFlowElicitationRequestedParams = ({
  chatId,
  entry,
}: BuildWorkspaceAgentGuidedFlowElicitationRequestedParams): UseTrackAgentElicitationRequestedParams => ({
  chat_id: chatId ?? '',
  component_type: 'guided_flow',
  elicitation_id: '',
  elicitation_type: 'guided_flow',
  entry,
  has_escape_hatch: false,
  step_index: WORKSPACE_AGENT_ELICITATION_STEP_INDEX,
});

export const buildWorkspaceAgentGuidedFlowElicitationRespondedParams = ({
  chatId,
  editId,
}: BuildWorkspaceAgentGuidedFlowElicitationRespondedParams): UseTrackAgentElicitationRespondedParams => ({
  chat_id: chatId ?? '',
  component_type: 'guided_flow',
  cta: 'approve',
  decision: 'n/a',
  elicitation_id: '',
  elicitation_type: 'guided_flow',
  remember_for_session: false,
  selected_count: 1,
  selected_option_ids: [editId],
  step_index: WORKSPACE_AGENT_ELICITATION_STEP_INDEX,
  used_escape_hatch: false,
});
